import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { friendlyErr } from '../../utils/formatters'
import {
  IconHeart, IconTrophy, IconZap, IconCheck, IconChevronRight, IconCopy, IconUser,
  IconSun, IconMoon
} from '../../components/ui/Icons'

const API_GRUPOS = import.meta.env.VITE_API_URL || '/api'

export function SidebarIconAmigos({ active, size = 16 }) {
  const c = active ? '#ff3b30' : 'var(--text-muted)'
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <circle cx="5.6" cy="5.8" r="2.3" stroke={c} strokeWidth="1.4" />
      <path d="M1.7 13.2c0-2.15 1.75-3.9 3.9-3.9s3.9 1.75 3.9 3.9" stroke={c} strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="11.4" cy="6.3" r="1.9" stroke={c} strokeWidth="1.4" />
      <path d="M11.9 9.6c1.5.5 2.6 1.9 2.6 3.6" stroke={c} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  )
}
export const SidebarIconGroups = SidebarIconAmigos

function IconMaximize({ size = 13, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
    </svg>
  )
}

function IconMinimize({ size = 13, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 14h6m0 0v6m0-6L3 21m17-7h-6m0 0v6m0-6l7 7M4 10h6m0 0V4m0 6L3 3m17 7h-6m0 0V4m0 6l7-7" />
    </svg>
  )
}

/**
 * Gráfico Sparkline ultra ligero y reactivo estilo Google Sheets =SPARKLINE()
 */
function MiniSparkline({ data, height = 24, strokeColor = '#007aff' }) {
  if (!data || data.length < 2) {
    return <span style={{ color: 'var(--text-faint)', fontSize: 11 }}>—</span>
  }
  const min = Math.min(...data)
  const max = Math.max(...data)
  const range = Math.max(2, max - min)
  const n = data.length
  const w = 100

  const points = data.map((v, i) => {
    const x = (i / (n - 1)) * (w - 8) + 4
    const y = height - 4 - ((v - min) / range) * (height - 8)
    return `${x.toFixed(1)},${y.toFixed(1)}`
  }).join(' ')

  const lastX = w - 4
  const lastY = height - 4 - ((data[data.length - 1] - min) / range) * (height - 8)

  return (
    <svg
      viewBox={`0 0 ${w} ${height}`}
      style={{ width: '100%', maxWidth: 110, height: height, display: 'inline-block', verticalAlign: 'middle', overflow: 'visible' }}
    >
      <polyline
        fill="none"
        stroke={strokeColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={points}
      />
      <circle cx={lastX} cy={lastY} r="2.5" fill={strokeColor} />
    </svg>
  )
}

function SortGlyph({ field, currentField, currentDir }) {
  if (field !== currentField) {
    return <span style={{ opacity: 0.25, marginLeft: 3, fontSize: 9.5 }}>↕</span>
  }
  return (
    <span style={{ color: '#007aff', marginLeft: 4, fontSize: 10, fontWeight: 900 }}>
      {currentDir === 'asc' ? '▲' : '▼'}
    </span>
  )
}

export function AmigosTab({ token, user, onNavigate }) {
  const isDemo = !!user?.demo
  const [groups, setGroups]   = useState(null)      // null = cargando
  const [error,  setError]    = useState(null)
  const [mode,   setMode]     = useState(null)      // null | 'crear' | 'unirme'
  const [name,   setName]     = useState('')
  const [code,   setCode]     = useState('')
  const [busy,   setBusy]     = useState(false)
  const [formMsg, setFormMsg] = useState(null)
  const [sel,    setSel]      = useState(null)      // id del grupo abierto
  const autoOpened = useRef(false)

  // Acceso exclusivo a modo demo para Matias
  const isMatias = Boolean(
    user?.email === 'matias@cagliero.ar' ||
    user?.email?.toLowerCase().includes('matias') ||
    user?.name?.toLowerCase().includes('matias') ||
    user?.id === 'f4fb9371-af01-4357-94e2-f0b88c080ebe'
  )

  const fetchGroups = useCallback(() => {
    if (isDemo || !token) return
    setError(null)
    fetch(`${API_GRUPOS}/groups`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async r => {
        const d = await r.json().catch(() => ({}))
        if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`)
        return d
      })
      .then(d => {
        setGroups(d.groups || [])
        // Si hay un solo grupo, lo abrimos automáticamente al entrar
        if (!autoOpened.current && d.groups && d.groups.length === 1) {
          autoOpened.current = true
          setSel(d.groups[0].id)
        }
      })
      .catch(e => {
        setError(friendlyErr(e))
        setGroups([])
      })
  }, [token, isDemo])

  useEffect(() => {
    fetchGroups()
  }, [fetchGroups])

  const crear = async () => {
    if (!name.trim() || busy) return
    setBusy(true)
    setFormMsg(null)
    try {
      const r = await fetch(`${API_GRUPOS}/groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: name.trim() }),
      })
      const d = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`)
      setName('')
      setMode(null)
      fetchGroups()
      if (d.group?.id) setSel(d.group.id)
    } catch (e) {
      setFormMsg(friendlyErr(e))
    } finally {
      setBusy(false)
    }
  }

  const unirme = async () => {
    if (!code.trim() || busy) return
    setBusy(true)
    setFormMsg(null)
    try {
      const r = await fetch(`${API_GRUPOS}/groups/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ code: code.trim().toUpperCase() }),
      })
      const d = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`)
      setCode('')
      setMode(null)
      fetchGroups()
      if (d.group?.id) setSel(d.group.id)
    } catch (e) {
      setFormMsg(friendlyErr(e))
    } finally {
      setBusy(false)
    }
  }

  const cardS = {
    background: 'var(--card-bg)',
    border: '1px solid var(--border-soft)',
    borderRadius: 16,
    padding: '16px 18px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
  }
  const primaryS = {
    background: '#007aff', color: '#fff', border: 'none',
    borderRadius: 10, padding: '10px 18px', fontSize: 13, fontWeight: 800,
    cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6
  }
  const ghostS = {
    background: 'none', color: 'var(--text-muted)', border: '1px solid var(--border)',
    borderRadius: 10, padding: '10px 16px', fontSize: 13, fontWeight: 700,
    cursor: 'pointer', fontFamily: 'inherit'
  }
  const inputS = {
    width: '100%', background: 'var(--field-bg)', border: '1px solid var(--field-border)',
    borderRadius: 10, padding: '10px 14px', fontSize: 14, color: 'var(--text)',
    outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit'
  }

  if (sel) {
    return (
      <GrupoDetalle
        token={token}
        user={user}
        groupId={sel}
        onBack={() => { setSel(null); fetchGroups() }}
        onLeft={() => { setSel(null); fetchGroups() }}
        onNavigate={onNavigate}
      />
    )
  }

  return (
    <div style={{ maxWidth: 840, margin: '0 auto', padding: '16px 14px 80px' }}>
      {/* Encabezado */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
        <div>
          <h2 style={{ fontSize: 24, fontWeight: 900, margin: 0, color: 'var(--text)', letterSpacing: -0.8 }}>
            Grupos en Vivo
          </h2>
          <p style={{ fontSize: 12.5, color: 'var(--text-faint)', margin: '4px 0 0' }}>
            Salas compartidas en tiempo real: meditación, coherencia y monitoreo multi-paciente con Polar H10.
          </p>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {isMatias && (
            <button
              onClick={() => setSel('demo-matias')}
              style={{
                background: 'rgba(88, 86, 214, 0.12)',
                border: '1px solid rgba(88, 86, 214, 0.3)',
                color: '#5856d6',
                borderRadius: 10,
                padding: '10px 14px',
                fontSize: 12.5,
                fontWeight: 800,
                cursor: 'pointer',
                fontFamily: 'inherit',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6
              }}
            >
              Probar Sala Demo en Vivo
            </button>
          )}
          <button onClick={() => { setMode('crear'); setFormMsg(null) }} style={primaryS}>
            + Crear grupo
          </button>
          <button onClick={() => { setMode('unirme'); setFormMsg(null) }} style={ghostS}>
            Unirme con código
          </button>
        </div>
      </div>

      {/* Modales / Formularios de creación o unirse */}
      {mode === 'crear' && (
        <div style={{ ...cardS, marginBottom: 18, border: '1.5px solid #007aff', animation: 'fadeIn 0.2s ease' }}>
          <p style={{ fontSize: 14, fontWeight: 800, margin: '0 0 10px', color: 'var(--text)' }}>
            Crear nuevo grupo
          </p>
          <input
            type="text" placeholder="Nombre del grupo (ej: Meditación Matutina, Terapia Grupal)"
            value={name} onChange={e => setName(e.target.value)}
            style={inputS} autoFocus maxLength={40}
          />
          {formMsg && <p style={{ fontSize: 12, color: '#ff3b30', margin: '6px 0 0' }}>{formMsg}</p>}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button onClick={crear} disabled={busy || !name.trim()} style={{ ...primaryS, opacity: busy || !name.trim() ? 0.5 : 1 }}>
              {busy ? 'Creando…' : 'Crear y abrir sala'}
            </button>
            <button onClick={() => { setMode(null); setFormMsg(null) }} style={ghostS}>Cancelar</button>
          </div>
        </div>
      )}

      {mode === 'unirme' && (
        <div style={{ ...cardS, marginBottom: 18, border: '1.5px solid #007aff', animation: 'fadeIn 0.2s ease' }}>
          <p style={{ fontSize: 14, fontWeight: 800, margin: '0 0 6px', color: 'var(--text)' }}>
            Unirte a un grupo existente
          </p>
          <p style={{ fontSize: 11.5, color: 'var(--text-faint)', margin: '0 0 10px' }}>
            Pedile al creador del grupo su código de 6 letras para entrar a la sala en vivo.
          </p>
          <input
            type="text" placeholder="Código de 6 letras (ej: POLAR1)"
            value={code} onChange={e => setCode(e.target.value.toUpperCase())}
            style={{ ...inputS, letterSpacing: 4, fontWeight: 800, textTransform: 'uppercase' }} autoFocus maxLength={6}
          />
          {formMsg && <p style={{ fontSize: 12, color: '#ff3b30', margin: '6px 0 0' }}>{formMsg}</p>}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button onClick={unirme} disabled={busy || code.trim().length !== 6} style={{ ...primaryS, opacity: busy || code.trim().length !== 6 ? 0.5 : 1 }}>
              {busy ? 'Verificando…' : 'Unirme a la sala'}
            </button>
            <button onClick={() => { setMode(null); setFormMsg(null) }} style={ghostS}>Cancelar</button>
          </div>
        </div>
      )}

      {/* Lista de Grupos */}
      {groups === null ? (
        <p style={{ color: 'var(--text-faint)', fontSize: 13, textAlign: 'center', padding: '30px 0' }}>Cargando grupos…</p>
      ) : groups.length === 0 ? (
        <div style={{ ...cardS, textAlign: 'center', padding: '40px 20px' }}>
          <div style={{
            width: 52, height: 52, borderRadius: '50%', background: 'rgba(0, 122, 255, 0.12)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14,
            color: '#007aff'
          }}>
            <SidebarIconAmigos active size={24} />
          </div>
          <p style={{ fontSize: 16, fontWeight: 800, color: 'var(--text)', margin: '0 0 6px' }}>
            Todavía no formás parte de ningún grupo
          </p>
          <p style={{ fontSize: 12.5, color: 'var(--text-faint)', margin: '0 auto 18px', maxWidth: 420 }}>
            Creá tu propio grupo o unite con un código para conectar los Polar H10 en vivo, meditar juntos o entrenar en equipo.
          </p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            <button onClick={() => setMode('crear')} style={primaryS}>Crear mi primer grupo</button>
            <button onClick={() => setMode('unirme')} style={ghostS}>Ingresar código</button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {groups.map(g => (
            <div
              key={g.id}
              onClick={() => setSel(g.id)}
              style={{
                ...cardS,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px 20px',
                transition: 'transform 0.15s, border-color 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = '#007aff'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-soft)'}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 12, background: 'rgba(0, 122, 255, 0.12)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#007aff',
                  fontWeight: 900, fontSize: 17
                }}>
                  {g.name[0]?.toUpperCase() || 'G'}
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--text)' }}>
                      {g.name}
                    </span>
                    <span style={{
                      fontSize: 10, fontWeight: 900, background: 'rgba(48, 209, 88, 0.15)',
                      color: '#30d158', padding: '2px 7px', borderRadius: 10, letterSpacing: 0.4
                    }}>
                      SALA EN VIVO
                    </span>
                  </div>
                  <span style={{ fontSize: 11.5, color: 'var(--text-faint)', display: 'block', marginTop: 2 }}>
                    Código: <strong style={{ letterSpacing: 1 }}>{g.code}</strong> · {g.members ?? 1} miembros
                  </span>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 12.5, fontWeight: 800, color: '#007aff' }}>
                  Entrar a la sala →
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * ── SALA EN VIVO DEL GRUPO (Formato Tabla Clínica Ordenable en Tiempo Real) ──
 */
function GrupoDetalle({ token, user, groupId, onBack, onLeft, onNavigate }) {
  const isDemoRoom = groupId === 'demo-matias'
  const isMatias = Boolean(
    user?.email === 'matias@cagliero.ar' ||
    user?.email?.toLowerCase().includes('matias') ||
    user?.name?.toLowerCase().includes('matias') ||
    user?.id === 'f4fb9371-af01-4357-94e2-f0b88c080ebe'
  )

  const [demoActive, setDemoActive] = useState(isDemoRoom)
  const [demoTick, setDemoTick] = useState(0)

  // Modo Pantalla Completa
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [fsTheme, setFsTheme] = useState('dark') // 'dark' | 'light'

  const toggleFullscreen = useCallback(async () => {
    if (!isFullscreen) {
      setIsFullscreen(true)
      try {
        if (document.documentElement.requestFullscreen) {
          await document.documentElement.requestFullscreen()
        }
      } catch (err) {}
    } else {
      setIsFullscreen(false)
      try {
        if (document.fullscreenElement && document.exitFullscreen) {
          await document.exitFullscreen()
        }
      } catch (err) {}
    }
  }, [isFullscreen])

  useEffect(() => {
    const onFsChange = () => {
      if (!document.fullscreenElement) {
        setIsFullscreen(false)
      }
    }
    document.addEventListener('fullscreenchange', onFsChange)
    return () => document.removeEventListener('fullscreenchange', onFsChange)
  }, [])

  // Estado de ordenamiento interactivo de columnas
  const [sortField, setSortField] = useState('live') // 'name' | 'live' | 'avg' | 'min' | 'max' | 'stress' | 'recovery' | 'posture'
  const [sortDir, setSortDir] = useState('desc') // 'asc' | 'desc'

  // Datos en vivo del servidor
  const [liveData, setLiveData] = useState(null)
  const [liveError, setLiveError] = useState(null)

  const [copied, setCopied]   = useState(null)
  const [leaving, setLeaving] = useState(false)
  const [confirmLeave, setConfirmLeave] = useState(false)

  // Buffer de muestras rodantes para calcular min, max, avg y sparkline
  const memberStatsRef = useRef({})

  // Timer para actualizar las fluctuaciones en modo demo
  useEffect(() => {
    if (!demoActive) return
    const interval = setInterval(() => setDemoTick(t => t + 1), 1500)
    return () => clearInterval(interval)
  }, [demoActive])

  // Polling continuo cada 1.5 segundos para telemetría en vivo real (si no es sala demo pura)
  useEffect(() => {
    if (isDemoRoom) return
    let dead = false
    const pollLive = async () => {
      try {
        const r = await fetch(`${API_GRUPOS}/groups/${groupId}/live`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        const d = await r.json().catch(() => ({}))
        if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`)
        if (!dead) {
          setLiveData(d)
          setLiveError(null)
        }
      } catch (e) {
        if (!dead) setLiveError(friendlyErr(e))
      }
    }

    pollLive()
    const timer = setInterval(pollLive, 1500)
    return () => { dead = true; clearInterval(timer) }
  }, [groupId, token, isDemoRoom])

  // Datos simulados cuando demoActive está encendido
  const effectiveData = useMemo(() => {
    if (!demoActive) return liveData

    const members = [
      {
        userId: user?.id || 'me',
        name: user?.name || 'Matias Cagliero',
        avatar: user?.avatar || null,
        me: true,
        isLive: true,
        hr: 61 + Math.round(Math.sin(demoTick * 0.35) * 3),
        rmssd: 58 + Math.round(Math.cos(demoTick * 0.2) * 4),
        coherence: 88 + Math.round(Math.sin(demoTick * 0.15) * 5),
        posture: 'sentado',
        steps: 1420,
      },
      {
        userId: 'demo-sofia',
        name: 'Sofía Valenzuela',
        avatar: null,
        me: false,
        isLive: true,
        hr: 57 + Math.round(Math.cos(demoTick * 0.3) * 3),
        rmssd: 72 + Math.round(Math.sin(demoTick * 0.2) * 5),
        coherence: 95,
        posture: 'acostado',
        steps: 840,
      },
      {
        userId: 'demo-lucas',
        name: 'Lucas Méndez',
        avatar: null,
        me: false,
        isLive: true,
        hr: 65 + Math.round(Math.sin(demoTick * 0.4) * 4),
        rmssd: 52 + Math.round(Math.cos(demoTick * 0.25) * 3),
        coherence: 83,
        posture: 'sentado',
        steps: 2150,
      },
      {
        userId: 'demo-camila',
        name: 'Camila Rossi',
        avatar: null,
        me: false,
        isLive: true,
        hr: 60 + Math.round(Math.cos(demoTick * 0.45) * 2),
        rmssd: 64 + Math.round(Math.sin(demoTick * 0.3) * 4),
        coherence: 90,
        posture: 'sentado',
        steps: 1680,
      }
    ]

    const groupAvgHr = Math.round(members.reduce((s, m) => s + m.hr, 0) / members.length)
    const groupAvgCoherence = Math.round(members.reduce((s, m) => s + m.coherence, 0) / members.length)

    return {
      groupId: groupId,
      groupName: isDemoRoom ? 'Sala Demo: Meditación y Coherencia' : (liveData?.groupName || 'Sala de Grupo'),
      code: isDemoRoom ? 'DEMO10' : (liveData?.code || 'DEMO'),
      isOwner: true,
      totalMembers: 4,
      activeCount: 4,
      groupAvgHr,
      groupAvgCoherence,
      members,
    }
  }, [demoActive, liveData, demoTick, user, groupId, isDemoRoom])

  // Calcular las estadísticas rodantes para la tabla clínica (min, max, avg, sparkline, estrés, recuperación)
  const membersWithStats = useMemo(() => {
    const rawMembers = effectiveData?.members || []

    return rawMembers.map(m => {
      const uid = m.userId || m.name
      if (!memberStatsRef.current[uid]) {
        memberStatsRef.current[uid] = {
          samples: m.hr ? [m.hr] : [],
        }
      }

      const st = memberStatsRef.current[uid]
      if (m.isLive && m.hr) {
        st.samples.push(m.hr)
        if (st.samples.length > 25) st.samples.shift()
      }

      const samples = st.samples.length ? st.samples : (m.hr ? [m.hr] : [])
      const avg = samples.length ? Math.round(samples.reduce((s, v) => s + v, 0) / samples.length) : (m.hr || null)
      const min = samples.length ? Math.min(...samples) : (m.hr || null)
      const max = samples.length ? Math.max(...samples) : (m.hr || null)

      // Estrés (0-100%): relación simpática (a mayor coherencia/RMSSD, menor estrés)
      const coh = m.coherence || 75
      const stressPct = Math.max(8, Math.min(92, Math.round(100 - coh * 0.75 - ((m.rmssd || 40) * 0.25))))
      
      // Recuperación (0-100%): parasimpático / tono vagal
      const recoveryPct = Math.max(12, Math.min(98, Math.round((coh * 0.65) + ((m.rmssd || 40) * 0.4))))

      // Segundos transcurridos desde el último latido / paquete
      const now = Date.now()
      let secondsAgo = null
      if (demoActive) {
        // En demo simula actualización continua fluida (0s a 2s)
        secondsAgo = (demoTick % 3)
      } else if (m.lastSeen) {
        secondsAgo = Math.max(0, Math.round((now - m.lastSeen) / 1000))
      } else if (m.isLive) {
        secondsAgo = 1
      }

      return {
        ...m,
        avgHr: avg,
        minHr: min,
        maxHr: max,
        sparklineData: samples.length >= 2 ? samples : (m.hr ? [m.hr - 1, m.hr, m.hr + 1, m.hr] : []),
        stressPct,
        recoveryPct,
        secondsAgo,
      }
    })
  }, [effectiveData, demoTick])

  // Ordenamiento interactivo por columna
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDir(field === 'name' ? 'asc' : 'desc')
    }
  }

  const sortedMembers = useMemo(() => {
    const list = [...membersWithStats]
    return list.sort((a, b) => {
      let valA, valB

      switch (sortField) {
        case 'name':
          valA = a.name.toLowerCase()
          valB = b.name.toLowerCase()
          return sortDir === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA)
        case 'live':
          valA = a.isLive ? (a.hr || 0) : -1
          valB = b.isLive ? (b.hr || 0) : -1
          break
        case 'avg':
          valA = a.avgHr || 0
          valB = b.avgHr || 0
          break
        case 'min':
          valA = a.minHr || 999
          valB = b.minHr || 999
          break
        case 'max':
          valA = a.maxHr || 0
          valB = b.maxHr || 0
          break
        case 'stress':
          valA = a.stressPct || 0
          valB = b.stressPct || 0
          break
        case 'recovery':
          valA = a.recoveryPct || 0
          valB = b.recoveryPct || 0
          break
        case 'posture':
          valA = a.posture || ''
          valB = b.posture || ''
          return sortDir === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA)
        case 'updated':
          valA = a.secondsAgo != null ? a.secondsAgo : 9999
          valB = b.secondsAgo != null ? b.secondsAgo : 9999
          break
        default:
          valA = a.hr || 0
          valB = b.hr || 0
      }

      if (valA < valB) return sortDir === 'asc' ? -1 : 1
      if (valA > valB) return sortDir === 'asc' ? 1 : -1
      return 0
    })
  }, [membersWithStats, sortField, sortDir])

  // Métricas colectivas generales del grupo (Pulso, Estrés y Recuperación)
  const groupStats = useMemo(() => {
    const active = membersWithStats.filter(m => m.isLive && m.hr)
    if (!active.length) {
      return {
        avgHr: null,
        avgStress: null,
        avgRecovery: null,
      }
    }
    const avgHr = Math.round(active.reduce((s, m) => s + m.hr, 0) / active.length)
    const avgStress = Math.round(active.reduce((s, m) => s + m.stressPct, 0) / active.length)
    const avgRecovery = Math.round(active.reduce((s, m) => s + m.recoveryPct, 0) / active.length)

    return {
      avgHr,
      avgStress,
      avgRecovery,
    }
  }, [membersWithStats])

  const g = effectiveData

  const copiarCodigo = async () => {
    if (!g?.code) return
    try {
      await navigator.clipboard.writeText(g.code)
      setCopied('code'); setTimeout(() => setCopied(null), 2000)
    } catch (e) {}
  }

  const invitar = async () => {
    if (!g?.code) return
    const text = `Sumate a mi sala en vivo "${g.groupName || g.name}" en Manabi con tu Polar H10. Entrá y usá el código ${g.code}.`
    if (navigator.share) {
      try { await navigator.share({ text }) } catch {}
    } else {
      try {
        await navigator.clipboard.writeText(text)
        setCopied('invite'); setTimeout(() => setCopied(null), 2500)
      } catch {}
    }
  }

  const salir = async () => {
    if (isDemoRoom) { onLeft(); return }
    if (leaving) return
    setLeaving(true)
    try {
      const r = await fetch(`${API_GRUPOS}/groups/${groupId}/leave`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`)
      onLeft()
    } catch (e) {
      setLeaving(false)
      setConfirmLeave(false)
    }
  }

  const cardS = {
    background: 'var(--card-bg)', border: '1px solid var(--border-soft)',
    borderRadius: 16, padding: '16px 18px', boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
  }

  const thStyle = {
    padding: '10px 10px',
    fontSize: 10.5,
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'background 0.15s',
  }

  return (
    <div
      style={isFullscreen ? {
        position: 'fixed',
        inset: 0,
        zIndex: 999999,
        background: fsTheme === 'dark' ? '#0a0d14' : '#f8fafc',
        color: fsTheme === 'dark' ? '#ffffff' : '#0f172a',
        padding: '18px 24px',
        overflowY: 'auto',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 16
      } : {
        width: '100%',
        maxWidth: '100%',
        margin: '0 auto',
        padding: '12px 14px 80px',
        boxSizing: 'border-box'
      }}
    >
      {/* Botón de retroceso */}
      {!isFullscreen && (
        <button
          onClick={onBack}
          style={{
            background: 'none', border: 'none', color: '#007aff', fontSize: 13,
            fontWeight: 800, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '4px 0', marginBottom: 12, fontFamily: 'inherit'
          }}
        >
          ← Volver a la lista de grupos
        </button>
      )}

      {/* Cabecera del Grupo */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <h2 style={{ fontSize: 24, fontWeight: 900, margin: 0, color: 'var(--text)', letterSpacing: -0.8 }}>
              {effectiveData?.groupName || 'Sala de Grupo'}
            </h2>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              fontSize: 10.5, fontWeight: 900, background: effectiveData?.activeCount > 0 ? 'rgba(48, 209, 88, 0.15)' : 'var(--chip-bg)',
              color: effectiveData?.activeCount > 0 ? '#30d158' : 'var(--text-faint)',
              padding: '3px 8px', borderRadius: 12
            }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: effectiveData?.activeCount > 0 ? '#30d158' : 'var(--text-faint)' }} />
              {effectiveData?.activeCount > 0 ? `${effectiveData.activeCount} en vivo` : 'Esperando pulsos'}
            </span>

            {/* Toggle de Modo Demo exclusivo para Matias */}
            {isMatias && (
              <button
                onClick={() => setDemoActive(v => !v)}
                style={{
                  background: demoActive ? '#5856d6' : 'rgba(88, 86, 214, 0.12)',
                  color: demoActive ? '#fff' : '#5856d6',
                  border: '1px solid rgba(88, 86, 214, 0.3)',
                  borderRadius: 8,
                  padding: '3px 9px',
                  fontSize: 10.5,
                  fontWeight: 800,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4
                }}
              >
                {demoActive ? '✓ Demo Activo (Matias)' : 'Simular participantes'}
              </button>
            )}
          </div>

          <p style={{ fontSize: 12, color: 'var(--text-faint)', margin: '4px 0 0' }}>
            Código de sala: <strong style={{ color: 'var(--text)', letterSpacing: 1 }}>{effectiveData?.code || '—'}</strong>
            <button onClick={copiarCodigo} style={{ background: 'none', border: 'none', color: '#007aff', cursor: 'pointer', fontSize: 11, fontWeight: 700, marginLeft: 8, padding: 0 }}>
              {copied === 'code' ? '✓ Copiado' : 'Copiar'}
            </button>
            <span style={{ margin: '0 6px', color: 'var(--border)' }}>·</span>
            <button onClick={invitar} style={{ background: 'none', border: 'none', color: '#007aff', cursor: 'pointer', fontSize: 11, fontWeight: 700, padding: 0 }}>
              {copied === 'invite' ? '✓ Copiado enlace' : 'Invitar amigos'}
            </button>
          </p>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* Si está en pantalla completa, selector de Modo Día / Noche */}
          {isFullscreen && (
            <button
              onClick={() => setFsTheme(t => t === 'dark' ? 'light' : 'dark')}
              style={{
                background: fsTheme === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.06)',
                border: `1px solid ${fsTheme === 'dark' ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.12)'}`,
                borderRadius: 8,
                color: fsTheme === 'dark' ? '#ffffff' : '#000000',
                padding: '6px 12px',
                fontSize: 11.5,
                fontWeight: 800,
                cursor: 'pointer',
                fontFamily: 'inherit',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5
              }}
            >
              {fsTheme === 'dark' ? (
                <>
                  <IconSun size={13} color="#ffffff" />
                  <span>Modo Día</span>
                </>
              ) : (
                <>
                  <IconMoon size={13} color="#000000" />
                  <span>Modo Noche</span>
                </>
              )}
            </button>
          )}

          {/* Botón de Pantalla Completa */}
          <button
            onClick={toggleFullscreen}
            style={{
              background: isFullscreen ? '#007aff' : 'rgba(0, 122, 255, 0.12)',
              color: isFullscreen ? '#ffffff' : '#007aff',
              border: `1px solid ${isFullscreen ? '#007aff' : 'rgba(0, 122, 255, 0.3)'}`,
              borderRadius: 8,
              padding: '6px 14px',
              fontSize: 11.5,
              fontWeight: 800,
              cursor: 'pointer',
              fontFamily: 'inherit',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6
            }}
            title={isFullscreen ? 'Salir de Pantalla Completa (Esc)' : 'Expandir a Pantalla Completa'}
          >
            {isFullscreen ? (
              <>
                <IconMinimize size={13} color="#ffffff" />
                <span>Salir Pantalla Completa</span>
              </>
            ) : (
              <>
                <IconMaximize size={13} color="#007aff" />
                <span>Pantalla Completa</span>
              </>
            )}
          </button>

          {!isFullscreen && (
            confirmLeave ? (
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={salir} disabled={leaving} style={{ background: '#ff3b30', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: 11, fontWeight: 800, cursor: 'pointer' }}>
                  {leaving ? 'Saliendo…' : 'Confirmar salir'}
                </button>
                <button onClick={() => setConfirmLeave(false)} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 8, padding: '6px 10px', fontSize: 11, color: 'var(--text-muted)', cursor: 'pointer' }}>
                  Cancelar
                </button>
              </div>
            ) : (
              <button onClick={() => setConfirmLeave(true)} style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text-faint)', borderRadius: 8, padding: '6px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                Salir del grupo
              </button>
            )
          )}
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          SALA EN VIVO PURA: MÉTRICAS COLECTIVAS, PACER Y TABLA CLÍNICA
      ───────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

        {/* Tarjeta Colectiva "El Pulso del Grupo": Pulso Promedio, Estrés General y Recuperación General */}
        <div style={{
          ...cardS,
          background: 'linear-gradient(135deg, var(--card-bg) 60%, rgba(0, 122, 255, 0.04) 100%)',
          border: '1.5px solid var(--border-soft)',
          padding: '18px 22px'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
            {/* 1. Pulso Promedio del Grupo */}
            <div>
              <span style={{ fontSize: 10.5, fontWeight: 800, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.7, display: 'block', marginBottom: 2 }}>
                Pulso Promedio del Grupo
              </span>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ fontSize: 28, fontWeight: 900, color: '#007aff', fontVariantNumeric: 'tabular-nums' }}>
                  {groupStats.avgHr ? `${groupStats.avgHr} bpm` : '—'}
                </span>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 700 }}>
                  promedio del grupo
                </span>
              </div>
            </div>

            {/* 2. Estrés General del Grupo */}
            <div>
              <span style={{ fontSize: 10.5, fontWeight: 800, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.7, display: 'block', marginBottom: 2 }}>
                Estrés General del Grupo
              </span>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{
                  fontSize: 28, fontWeight: 900, fontVariantNumeric: 'tabular-nums',
                  color: groupStats.avgStress == null ? 'var(--text-faint)' : (groupStats.avgStress < 25 ? '#30d158' : groupStats.avgStress < 50 ? '#ff9f0a' : '#ff3b30')
                }}>
                  {groupStats.avgStress != null ? `${groupStats.avgStress}%` : '—'}
                </span>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 700 }}>
                  {groupStats.avgStress == null ? '' : (groupStats.avgStress < 25 ? 'Calma profunda' : groupStats.avgStress < 50 ? 'Moderado' : 'Elevado')}
                </span>
              </div>
            </div>

            {/* 3. Recuperación General del Grupo */}
            <div>
              <span style={{ fontSize: 10.5, fontWeight: 800, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.7, display: 'block', marginBottom: 2 }}>
                Recuperación General del Grupo
              </span>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{
                  fontSize: 28, fontWeight: 900, fontVariantNumeric: 'tabular-nums',
                  color: groupStats.avgRecovery == null ? 'var(--text-faint)' : (groupStats.avgRecovery >= 80 ? '#30d158' : groupStats.avgRecovery >= 60 ? '#007aff' : '#ff9f0a')
                }}>
                  {groupStats.avgRecovery != null ? `${groupStats.avgRecovery}%` : '—'}
                </span>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 700 }}>
                  {groupStats.avgRecovery == null ? '' : (groupStats.avgRecovery >= 80 ? 'Óptima (Vagal)' : groupStats.avgRecovery >= 60 ? 'Buena' : 'Baja')}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Tabla Clínica en Vivo Ordenable por Headers (Google Sheets Style) ── */}
        <div style={{ ...cardS, padding: '14px 16px', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontSize: 11.5, fontWeight: 800, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.6 }}>
              Monitor en Tiempo Real de Integrantes ({sortedMembers.length})
            </span>
            <span style={{ fontSize: 10.5, color: 'var(--text-faint)' }}>
              Tocá cualquier columna para ordenar · Actualizando cada 1.5s
            </span>
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 740 }}>
            <thead>
              <tr style={{ borderBottom: '1.5px solid var(--border)' }}>
                {/* Integrante */}
                <th
                  onClick={() => handleSort('name')}
                  style={{ ...thStyle, textAlign: 'left', color: sortField === 'name' ? 'var(--text)' : 'var(--text-faint)' }}
                  title="Ordenar por Nombre"
                >
                  Integrante <SortGlyph field="name" currentField={sortField} currentDir={sortDir} />
                </th>

                {/* En Vivo */}
                <th
                  onClick={() => handleSort('live')}
                  style={{ ...thStyle, textAlign: 'center', color: sortField === 'live' ? '#ff3b30' : '#ff3b30' }}
                  title="Ordenar por Pulso en Vivo"
                >
                  En Vivo <SortGlyph field="live" currentField={sortField} currentDir={sortDir} />
                </th>

                {/* Promedio */}
                <th
                  onClick={() => handleSort('avg')}
                  style={{ ...thStyle, textAlign: 'center', color: sortField === 'avg' ? 'var(--text)' : 'var(--text-faint)' }}
                  title="Ordenar por Promedio de Frecuencia"
                >
                  Promedio <SortGlyph field="avg" currentField={sortField} currentDir={sortDir} />
                </th>

                {/* Mínima */}
                <th
                  onClick={() => handleSort('min')}
                  style={{ ...thStyle, textAlign: 'center', color: sortField === 'min' ? '#007aff' : '#007aff' }}
                  title="Ordenar por Mínima Frecuencia (Nadir)"
                >
                  Mínima <SortGlyph field="min" currentField={sortField} currentDir={sortDir} />
                </th>

                {/* Máxima */}
                <th
                  onClick={() => handleSort('max')}
                  style={{ ...thStyle, textAlign: 'center', color: sortField === 'max' ? '#ff9f0a' : '#ff9f0a' }}
                  title="Ordenar por Máxima Frecuencia"
                >
                  Máxima <SortGlyph field="max" currentField={sortField} currentDir={sortDir} />
                </th>

                {/* Trend (Sheets) */}
                <th style={{ ...thStyle, textAlign: 'center', color: 'var(--text-faint)', cursor: 'default' }}>
                  Trend (Sheets)
                </th>

                {/* Estrés */}
                <th
                  onClick={() => handleSort('stress')}
                  style={{ ...thStyle, textAlign: 'center', color: sortField === 'stress' ? 'var(--text)' : 'var(--text-faint)' }}
                  title="Ordenar por Nivel de Estrés"
                >
                  Estrés <SortGlyph field="stress" currentField={sortField} currentDir={sortDir} />
                </th>

                {/* Recuperación */}
                <th
                  onClick={() => handleSort('recovery')}
                  style={{ ...thStyle, textAlign: 'center', color: sortField === 'recovery' ? '#30d158' : '#30d158' }}
                  title="Ordenar por Nivel de Recuperación"
                >
                  Recuperación <SortGlyph field="recovery" currentField={sortField} currentDir={sortDir} />
                </th>

                {/* Postura */}
                <th
                  onClick={() => handleSort('posture')}
                  style={{ ...thStyle, textAlign: 'right', color: sortField === 'posture' ? 'var(--text)' : 'var(--text-faint)' }}
                  title="Ordenar por Postura"
                >
                  Postura <SortGlyph field="posture" currentField={sortField} currentDir={sortDir} />
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedMembers.map(m => {
                const isLive = m.isLive

                // Diagnóstico de estrés
                const stressColor = m.stressPct < 25 ? '#30d158' : m.stressPct < 50 ? '#ff9f0a' : '#ff3b30'
                const stressLabel = m.stressPct < 25 ? 'Bajo' : m.stressPct < 50 ? 'Medio' : 'Alto'

                // Diagnóstico de recuperación
                const recColor = m.recoveryPct >= 80 ? '#30d158' : m.recoveryPct >= 60 ? '#007aff' : '#ff9f0a'
                const recLabel = m.recoveryPct >= 80 ? 'Óptima' : m.recoveryPct >= 60 ? 'Buena' : 'Baja'

                return (
                  <tr
                    key={m.userId}
                    style={{
                      borderBottom: '1px solid var(--border-soft)',
                      background: m.me ? 'rgba(0, 122, 255, 0.04)' : 'transparent',
                      transition: 'background 0.15s ease'
                    }}
                  >
                    {/* Nombre y avatar */}
                    <td style={{ padding: '10px 10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                        <div style={{
                          width: 32, height: 32, borderRadius: '50%',
                          background: isLive ? 'rgba(0, 122, 255, 0.15)' : 'var(--chip-bg)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          border: isLive ? '2px solid #30d158' : '1px solid var(--border)',
                          overflow: 'hidden', fontWeight: 800, fontSize: 12, color: '#007aff', flexShrink: 0
                        }}>
                          {m.avatar ? <img src={m.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : m.name[0]?.toUpperCase()}
                        </div>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--text)', whiteSpace: 'nowrap' }}>
                              {m.name}
                            </span>
                            {m.me && (
                              <span style={{ fontSize: 9, fontWeight: 900, background: 'var(--chip-bg)', padding: '1px 4px', borderRadius: 4, color: 'var(--text-faint)' }}>
                                Tú
                              </span>
                            )}
                          </div>
                          <span style={{ fontSize: 10, color: isLive ? '#30d158' : 'var(--text-faint)', fontWeight: 700, display: 'block' }}>
                            {isLive ? `● En vivo (${m.secondsAgo != null ? `${m.secondsAgo}s` : 'ahora'})` : 'Desconectado'}
                          </span>
                        </div>
                      </div>
                    </td>

                    {/* Pulso en Vivo */}
                    <td style={{ textAlign: 'center', padding: '10px 10px' }}>
                      {isLive ? (
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                          <span style={{
                            width: 7, height: 7, borderRadius: '50%', background: '#ff3b30',
                            animation: 'pulseHeart 0.9s infinite'
                          }} />
                          <span style={{ fontSize: 16, fontWeight: 900, color: '#ff3b30', fontVariantNumeric: 'tabular-nums' }}>
                            {m.hr}
                          </span>
                          <span style={{ fontSize: 10, color: 'var(--text-faint)', fontWeight: 700 }}>bpm</span>
                        </div>
                      ) : (
                        <span style={{ color: 'var(--text-faint)', fontSize: 13 }}>—</span>
                      )}
                    </td>

                    {/* Promedio FC */}
                    <td style={{ textAlign: 'center', padding: '10px 10px', fontSize: 13.5, fontWeight: 800, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
                      {m.avgHr ? `${m.avgHr} bpm` : '—'}
                    </td>

                    {/* Mínima FC */}
                    <td style={{ textAlign: 'center', padding: '10px 10px', fontSize: 13, fontWeight: 800, color: '#007aff', fontVariantNumeric: 'tabular-nums' }}>
                      {m.minHr ? `${m.minHr} bpm` : '—'}
                    </td>

                    {/* Máxima FC */}
                    <td style={{ textAlign: 'center', padding: '10px 10px', fontSize: 13, fontWeight: 800, color: '#ff9f0a', fontVariantNumeric: 'tabular-nums' }}>
                      {m.maxHr ? `${m.maxHr} bpm` : '—'}
                    </td>

                    {/* Sparkline estilo Sheets */}
                    <td style={{ textAlign: 'center', padding: '10px 10px' }}>
                      <MiniSparkline data={m.sparklineData} strokeColor={isLive ? '#007aff' : 'var(--text-faint)'} />
                    </td>

                    {/* Métrica de Estrés */}
                    <td style={{ textAlign: 'center', padding: '10px 10px' }}>
                      {isLive ? (
                        <span style={{
                          display: 'inline-block', fontSize: 11, fontWeight: 800, padding: '2px 8px', borderRadius: 8,
                          background: `${stressColor}18`, color: stressColor, border: `1px solid ${stressColor}35`,
                          fontVariantNumeric: 'tabular-nums'
                        }}>
                          {m.stressPct}% · {stressLabel}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--text-faint)', fontSize: 12 }}>—</span>
                      )}
                    </td>

                    {/* Métrica de Recuperación */}
                    <td style={{ textAlign: 'center', padding: '10px 10px' }}>
                      {isLive ? (
                        <span style={{
                          display: 'inline-block', fontSize: 11, fontWeight: 800, padding: '2px 8px', borderRadius: 8,
                          background: `${recColor}18`, color: recColor, border: `1px solid ${recColor}35`,
                          fontVariantNumeric: 'tabular-nums'
                        }}>
                          {m.recoveryPct}% · {recLabel}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--text-faint)', fontSize: 12 }}>—</span>
                      )}
                    </td>

                    {/* Postura */}
                    <td style={{ textAlign: 'right', padding: '10px 10px' }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'capitalize' }}>
                        {m.posture || 'Sentado'}
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Estilos CSS para animaciones de latido y respirador */}
      <style>{`
        @keyframes pulseHeart {
          0% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.35); opacity: 0.8; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes pacerBreathe {
          0% { transform: scale(0.8); opacity: 0.5; }
          50% { transform: scale(1.4); opacity: 1; }
          100% { transform: scale(0.8); opacity: 0.5; }
        }
      `}</style>
    </div>
  )
}
